import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { DeadLetterListItem } from '../../models/dead-letter-list-item.model';
import type { DeadLetterQuery, DeadLetterBatchRequest, RemoveDeadLettersResult, ReplayDeadLettersResult } from '../../models/dead-letter-query.model';
import type { DeadLetterBulkAction } from '../dead-letter-bulk-actions/dead-letter-bulk-actions.component';
import { DeadLetterService } from '../../services/dead-letter.service';
import { DeadLetterDetailDialogComponent } from '../dead-letter-detail-dialog/dead-letter-detail-dialog.component';
import { DeadLetterFilterState } from '../dead-letter-filters/dead-letter-filters.component';
import { DeadLetterListComponent, SelectionChangeEvent } from '../dead-letter-list/dead-letter-list.component';

@Component({
    selector: 'app-dead-letters',
    templateUrl: './dead-letters.component.html',
    styleUrls: ['./dead-letters.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DeadLettersComponent implements OnInit {
  items: DeadLetterListItem[] = [];
  selectedIds = new Set<string>();
  selectAll = false;
  filters: DeadLetterFilterState = { searchTerm: '', eventType: '', dateFrom: null, dateTo: null };
  nextPage?: string;

  totalCount = 0;
  initialLoading = false;
  appendLoading = false;
  bulkActionInProgress: DeadLetterBulkAction | null = null;
  initialError: string | null = null;
  actionInProgressIds = new Set<string>();
  private snapshotTs?: number;

  private requestGeneration = 0;

  @ViewChild(DeadLetterListComponent) private listComponent?: DeadLetterListComponent;

  constructor(
    private readonly deadLetterService: DeadLetterService,
    private readonly dialog: MatDialog,
    private readonly snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.loadFirstPage();
  }

  get selectedCount(): number {
    return this.selectAll ? this.totalCount : this.selectedIds.size;
  }

  onFiltersChange(filters: DeadLetterFilterState): void {
    this.filters = filters;
    this.resetAndLoadFirstPage();
  }

  onSelectionChange(event: SelectionChangeEvent): void {
    if (this.selectAll && !event.selected) {
      this.selectAll = false;
      this.selectedIds = new Set(this.items.map(i => i.id));
      this.selectedIds.delete(event.id);
      this.selectedIds = new Set(this.selectedIds);
      return;
    }

    if (event.selected) {
      this.selectedIds.add(event.id);
    } else {
      this.selectedIds.delete(event.id);
    }
    this.selectedIds = new Set(this.selectedIds);
  }

  onSelectAllChange(checked: boolean): void {
    this.selectAll = checked;
    this.selectedIds = new Set();
  }

  onLoadMore(): void {
    if (this.appendLoading || !this.nextPage) {
      return;
    }
    this.appendLoading = true;
    const generation = this.requestGeneration;
    const query = this.buildQuery(this.nextPage);
    this.deadLetterService.getDeadLetters(query).subscribe({
      next: (response) => {
        if (generation !== this.requestGeneration) { return; }
        this.items = [...this.items, ...response.items];
        this.nextPage = response.nextPage;
        this.updateSelectAllState();
        this.appendLoading = false;
      },
      error: () => {
        if (generation !== this.requestGeneration) { return; }
        this.appendLoading = false;
        this.showAppendErrorSnackbar();
      }
    });
  }

  onBulkAction(action: DeadLetterBulkAction): void {
    this.bulkActionInProgress = action;
    const request = this.buildBatchRequest();
    const handleBulkAction = <T>(showSuccess: (result: T) => void) => ({
      next: (result: T) => {
        this.bulkActionInProgress = null;
        showSuccess(result);
        this.resetAndLoadFirstPage();
      },
      error: () => {
        this.bulkActionInProgress = null;
        this.showActionErrorSnackbar();
      }
    });

    if (action === 'resubmit') {
      this.deadLetterService.replayDeadLetters(request).subscribe(
        handleBulkAction<ReplayDeadLettersResult>(result => this.showReplaySuccessSnackbar(result))
      );
    } else if (action === 'remove') {
      this.deadLetterService.removeDeadLetters(request).subscribe(
        handleBulkAction<RemoveDeadLettersResult>(result => this.showRemoveSuccessSnackbar(result))
      );
    }
  }

  onIdClick(id: string): void {
    const dialogRef = this.dialog.open(DeadLetterDetailDialogComponent, {
      data: { id },
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'dead-letter-detail-dialog-panel',
      width: '96vw',
      height: '92vh',
      maxWidth: '96vw',
      maxHeight: '92vh'
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'resubmitted') {
        this.showReplaySuccessSnackbar({ replayed: 1, failed: 0, notFound: 0 });
        this.resetAndLoadFirstPage();
      } else if (result?.removed > 0) {
        this.showRemoveSuccessSnackbar({ removed: result.removed, notFound: 0 });
        this.resetAndLoadFirstPage();
      }
    });
  }

  onRemoveClick(id: string): void {
    this.addActionInProgress(id);
    this.deadLetterService.removeDeadLetters({ ids: [id] }).subscribe({
      next: (result) => {
        this.removeActionInProgress(id);
        this.showRemoveSuccessSnackbar(result);
        this.resetAndLoadFirstPage();
      },
      error: () => {
        this.removeActionInProgress(id);
        this.showActionErrorSnackbar();
      }
    });
  }

  onReplayClick(id: string): void {
    this.addActionInProgress(id);
    this.deadLetterService.replayDeadLetters({ ids: [id] }).subscribe({
      next: (result) => {
        this.removeActionInProgress(id);
        if (result.failed > 0) {
          this.showActionErrorSnackbar();
          return;
        }
        this.showReplaySuccessSnackbar(result);
        this.resetAndLoadFirstPage();
      },
      error: () => {
        this.removeActionInProgress(id);
        this.showActionErrorSnackbar();
      }
    });
  }

  loadFirstPage(): void {
    this.appendLoading = false;
    this.initialLoading = true;
    this.initialError = null;
    const generation = ++this.requestGeneration;
    const query = this.buildQuery();
    this.deadLetterService.getDeadLetters(query).subscribe({
      next: (response) => {
        if (generation !== this.requestGeneration) { return; }
        this.items = response.items;
        this.nextPage = response.nextPage;
        this.totalCount = response.totalCount ?? 0;
        this.snapshotTs = response.snapshotTs;
        this.updateSelectAllState();
        this.initialLoading = false;
      },
      error: () => {
        if (generation !== this.requestGeneration) { return; }
        this.initialError = 'DEAD_LETTERS.ERRORS.INITIAL_LOAD_FAILED';
        this.initialLoading = false;
      }
    });
  }

  private resetAndLoadFirstPage(): void {
    this.items = [];
    this.nextPage = undefined;
    this.selectedIds = new Set();
    this.selectAll = false;
    this.actionInProgressIds = new Set();
    this.scrollToTop();
    this.loadFirstPage();
  }

  private addActionInProgress(id: string): void {
    this.actionInProgressIds = new Set(this.actionInProgressIds).add(id);
  }

  private removeActionInProgress(id: string): void {
    const next = new Set(this.actionInProgressIds);
    next.delete(id);
    this.actionInProgressIds = next;
  }

  private scrollToTop(): void {
    if (this.listComponent) {
      this.listComponent.scrollToTop();
    }
  }

  private updateSelectAllState(): void {
    if (!this.selectAll) { return; }
    this.selectAll = this.items.length > 0;
  }

  private buildQuery(nextPage?: string): DeadLetterQuery {
    return {
      searchTerm: this.filters.searchTerm,
      eventType: this.filters.eventType,
      dateFrom: this.filters.dateFrom,
      dateTo: this.filters.dateTo,
      nextPage
    };
  }

  private showAppendErrorSnackbar(): void {
    this.snackbarService.error('DEAD_LETTERS.ERRORS.APPEND_LOAD_FAILED');
  }

  private showActionErrorSnackbar(): void {
    this.snackbarService.error('DEAD_LETTERS.ERRORS.ACTION_FAILED');
  }

  private showRemoveSuccessSnackbar(result: RemoveDeadLettersResult): void {
    const hasFailures = result.notFound > 0;
    const key = hasFailures
      ? 'DEAD_LETTERS.SNACKBAR.REMOVE_PARTIAL'
      : 'DEAD_LETTERS.SNACKBAR.REMOVED';
    const method = hasFailures ? 'error' : 'success';
    this.snackbarService[method](key, { count: result.removed, ...result });
  }

  private showReplaySuccessSnackbar(result: ReplayDeadLettersResult): void {
    const hasFailures = result.failed > 0 || result.notFound > 0;
    const key = hasFailures
      ? 'DEAD_LETTERS.SNACKBAR.REPLAY_PARTIAL'
      : 'DEAD_LETTERS.SNACKBAR.REPLAYED';
    const method = hasFailures ? 'error' : 'success';
    this.snackbarService[method](key, { count: result.replayed, ...result });
  }

  private buildBatchRequest(): DeadLetterBatchRequest {
    if (this.selectAll) {
      return {
        searchCriteria: {
          searchText: this.filters.searchTerm || undefined,
          eventType: this.filters.eventType || undefined,
          from: this.filters.dateFrom?.toISOString(),
          to: this.filters.dateTo?.toISOString()
        },
        snapshotTs: this.snapshotTs
      };
    }
    return { ids: Array.from(this.selectedIds) };
  }
}